import React, { useMemo } from 'react';
import { Grid } from '@mui/material';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import TrendingDownOutlinedIcon from '@mui/icons-material/TrendingDownOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import { FP, StatCard } from 'components/financepro';
import EstadoFinancieroShell, {
  fmtMoney,
  fmtMontoContable,
  textoPeriodoRango,
} from './EstadoFinancieroShell';
import PlantillaEstadoFinanciero, { LAYOUT_ESTADO_SIMPLE } from './PlantillaEstadoFinanciero';
import { buildPlantillaEstadoFinancieroPrint } from './plantillaEstadoFinancieroPrint';

/** Clave del programa para validar permisos (mismo patrón que FacturaPOS). */
export const PROGRAMA_ESTADO_DE_RESULTADOS = 'estadoderesultados';

const COLUMNAS = [
  { id: 'cuenta', label: 'Cuenta', tipo: 'cuenta', ocultarEnMovil: true },
  { id: 'nombre', label: 'Concepto', tipo: 'texto' },
  { id: 'monto', label: 'Monto', tipo: 'monto' },
];

const etiquetaSeccion = (titulo) => {
  const t = String(titulo || '').toLowerCase();
  if (t.includes('ingreso')) return { signo: '', subtitulo: 'Ingresos del período' };
  if (t.includes('costo')) return { signo: '(−)', subtitulo: 'Costo de ventas y costos operativos' };
  if (t.includes('gasto')) return { signo: '(−)', subtitulo: 'Gastos de administración, ventas y financieros' };
  return { signo: '', subtitulo: '' };
};

const agruparEstado = (filas) => {
  const secciones = [];
  let utilidad = null;
  let actual = null;

  (filas || []).forEach((row) => {
    if (row.es_utilidad || (row.es_total && !row.es_total_seccion)) {
      utilidad = row;
      return;
    }
    if (row.es_titulo) {
      actual = {
        titulo: row.nombre || '',
        cuentas: [],
        total: 0,
      };
      secciones.push(actual);
      return;
    }
    if (row.es_total_seccion && actual) {
      actual.total = parseFloat(row.monto) || 0;
      return;
    }
    if (actual) {
      actual.cuentas.push(row);
    }
  });

  const totalDe = (nombre) => {
    const sec = secciones.find((s) =>
      String(s.titulo || '').toLowerCase().includes(nombre)
    );
    return sec ? sec.total : 0;
  };

  const ingresos = totalDe('ingreso');
  const costos = totalDe('costo');
  const gastos = totalDe('gasto');
  const utilidadBruta = Math.round((ingresos - costos) * 100) / 100;
  const utilidadNeta =
    utilidad != null
      ? parseFloat(utilidad.monto) || 0
      : Math.round((utilidadBruta - gastos) * 100) / 100;

  return { secciones, utilidad, ingresos, costos, gastos, utilidadBruta, utilidadNeta };
};

export const construirModeloEstadoResultados = ({
  filas,
  fechaDesde,
  fechaHasta,
  nombreEmpresa,
}) => {
  const { secciones, utilidadBruta, utilidadNeta } = agruparEstado(filas);
  const perdida = utilidadNeta < 0;

  return {
    nombreEmpresa,
    tituloDocumento: 'Estado de resultados',
    periodo: textoPeriodoRango(fechaDesde, fechaHasta),
    columnas: COLUMNAS,
    layout: LAYOUT_ESTADO_SIMPLE,
    mensajeVacio: 'Sin movimientos en esta sección',
    secciones: secciones.map((s) => {
      const meta = etiquetaSeccion(s.titulo);
      const modelo = {
        titulo: meta.signo ? `${meta.signo} ${s.titulo}` : s.titulo,
        subtitulo: meta.subtitulo,
        filas: s.cuentas.map((c) => ({
          cuenta: c.cuenta,
          nombre: c.nombre,
          celdas: { monto: c.monto },
        })),
        total: { etiqueta: `Total ${s.titulo}`, celdas: { monto: s.total } },
      };
      if (String(s.titulo || '').toLowerCase().includes('costo')) {
        modelo.despues = [
          {
            etiqueta: 'Utilidad bruta',
            variante: 'intermedio',
            celdas: { monto: utilidadBruta },
          },
        ];
      }
      return modelo;
    }),
    totales: [
      {
        etiqueta: perdida ? 'Pérdida neta del período' : 'Utilidad neta del período',
        variante: 'neto',
        tono: perdida ? 'alerta' : 'ok',
        celdas: { monto: utilidadNeta },
      },
    ],
    nota: `Ingresos − Costos − Gastos = ${perdida ? 'Pérdida' : 'Utilidad'} neta · Los importes negativos se muestran entre paréntesis`,
    firmas: [{ etiqueta: 'Preparado por' }, { etiqueta: 'Revisado por' }],
  };
};

const CuerpoEstadoResultados = (ctx) => {
  const modelo = useMemo(
    () => construirModeloEstadoResultados(ctx),
    [ctx.filas, ctx.fechaDesde, ctx.fechaHasta, ctx.nombreEmpresa]
  );
  return <PlantillaEstadoFinanciero {...modelo} />;
};

const ResumenEstadoResultados = ({ filas }) => {
  const { ingresos, costos, gastos, utilidadNeta } = agruparEstado(filas);
  const perdida = utilidadNeta < 0;

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          label="Ingresos"
          value={`$${fmtMoney(ingresos)}`}
          icon={TrendingUpOutlinedIcon}
          accent={FP.color.accentBlue}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          label="Costos"
          value={`$${fmtMoney(costos)}`}
          icon={PaymentsOutlinedIcon}
          accent={FP.color.warning}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          label="Gastos"
          value={`$${fmtMoney(gastos)}`}
          icon={TrendingDownOutlinedIcon}
          accent={FP.color.navyMuted}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          label={perdida ? 'Pérdida neta' : 'Utilidad neta'}
          value={`$${fmtMontoContable(utilidadNeta)}`}
          icon={AccountBalanceOutlinedIcon}
          accent={perdida ? FP.color.danger : FP.color.success}
        />
      </Grid>
    </Grid>
  );
};

const columns = [
  {
    id: 'nombre',
    label: 'Concepto',
    align: 'left',
    width: '72%',
    render: (_, row) => {
      if (row.es_titulo) return String(row.nombre || '').toUpperCase();
      if (row.cuenta) return `${row.cuenta}  ${row.nombre || ''}`;
      return row.nombre || '';
    },
  },
  {
    id: 'monto',
    label: 'Monto',
    align: 'right',
    width: '28%',
    render: (v, row) => (row.es_titulo ? '' : `$${fmtMontoContable(v)}`),
  },
];

const EstadoDeResultados = () => (
  <EstadoFinancieroShell
    programa={PROGRAMA_ESTADO_DE_RESULTADOS}
    title="Estado de Resultados"
    subtitle="Ingresos, costos y gastos del período"
    reportTitle="Estado de Resultados"
    IconComponent={AssessmentOutlinedIcon}
    endpoint="contabilidadbackend_estadoderesultados"
    columns={columns}
    emptyMessage="No hay movimientos de resultado para el período seleccionado"
    mapResponseRows={(data) => data.lineas || data.filas || []}
    hideFooter
    footerFromRows={(filas) => {
      const util = filas.find((r) => r.es_utilidad || r.es_total);
      if (!util) return '';
      const n = parseFloat(util.monto) || 0;
      return `${n < 0 ? 'Pérdida' : 'Utilidad'} neta: $${fmtMontoContable(n)}`;
    }}
    renderSummary={(ctx) => <ResumenEstadoResultados filas={ctx.filas} />}
    renderBody={(ctx) => <CuerpoEstadoResultados {...ctx} />}
    buildPrintHtml={(ctx) => buildPlantillaEstadoFinancieroPrint(construirModeloEstadoResultados(ctx))}
  />
);

export default EstadoDeResultados;
